/**
 * 테마(다크 모드) 훅
 *
 * Redux 스토어의 테마 설정을 기반으로 다크/라이트 모드를 전환한다.
 * html[data-theme] 속성을 설정하여 CSS 변수 기반 테마 전환을 트리거.
 * useLanguage 훅과 동일한 패턴.
 */
import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setTheme as setThemeAction, selectTheme } from '../store/authSlice';

/**
 * 다크모드 전역 스위치 (2026-08-26 비활성화)
 *
 * 다크모드 미대응 화면이 다수 발견되어(디자인 감사 DESIGN_AUDIT_260826.md §1)
 * 대응 완료 전까지 라이트 모드로 고정한다. 재활성화: true로 변경 +
 * public/index.html의 테마 선주입 스크립트 주석 해제.
 */
export const DARK_MODE_ENABLED = false;

/**
 * 테마 훅
 * @returns {{ theme: string, isDark: boolean, setTheme: Function, toggleTheme: Function }}
 */
const useTheme = () => {
  const dispatch = useDispatch();
  const storedTheme = useSelector(selectTheme);
  const theme = DARK_MODE_ENABLED ? storedTheme : 'light';
  const isDark = theme === 'dark';

  // html data-theme 속성 동기화
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  /** 테마 변경 */
  const setTheme = useCallback(
    (t) => {
      dispatch(setThemeAction(t));
    },
    [dispatch]
  );

  /** 테마 토글 (다크모드 비활성 시 no-op) */
  const toggleTheme = useCallback(() => {
    if (!DARK_MODE_ENABLED) return;
    dispatch(setThemeAction(isDark ? 'light' : 'dark'));
  }, [dispatch, isDark]);

  return {
    theme,
    isDark,
    setTheme,
    toggleTheme,
  };
};

export default useTheme;
